import React from "react";
import { Link } from "react-router-dom";

import RightArrow from "../SVG";
import Image from "../../assets/img/event/event-3-1.jpg";

const SingleEvent = (props) => {
  const {
    eventId,
    itemClass,
    eventImage,
    eventDate,
    eventMonth,
    title,
    description,
    eventTime,
    eventLocation,
    hasArrow,
    eventYear,
  } = props;

  // Inline CSS styles - FIXED VERSION
  const styles = {
    eventItemBox: {
      height: "100%",
      marginBottom: "30px",
    },
    eventItem: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
    },
    eventItemHover: {
      transform: "translateY(-5px)",
      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
    },
    eventThumb: {
      position: "relative",
      overflow: "hidden",
      height: "220px", // FIXED: Added fixed height to container
      width: "100%",
    },
    eventImage: {
      height: "100%", // FIXED: Changed from "220px" to "100%"
      width: "100%",
      objectFit: "cover", // FIXED: Was "fit cover" - now correct syntax
      objectPosition: "center",
      display: "block",
      transition: "transform 0.3s ease",
    },
    eventImageHover: {
      transform: "scale(1.05)",
    },
    eventDate: {
      position: "absolute",
      top: "15px",
      right: "15px",
      background: "linear-gradient(135deg, #7c3aed, #a855f7)",
      color: "white",
      padding: "10px 15px",
      borderRadius: "8px",
      textAlign: "center",
      boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
      zIndex: 2,
    },
    eventDateSpan: {
      display: "block",
      fontSize: "12px",
      fontWeight: "600",
      lineHeight: "1.2",
    },
    eventDateNumber: {
      fontSize: "18px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    eventContent: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "25px",
    },
    eventTitle: {
      margin: "0 0 15px 0",
      fontSize: "18px",
      fontWeight: "600",
      lineHeight: "1.4",
      color: "#1f2937",
    },
    eventTitleLink: {
      color: "inherit",
      textDecoration: "none",
      transition: "color 0.3s ease",
    },
    eventTitleLinkHover: {
      color: "#7c3aed",
    },
    eventDescription: {
      flex: 1,
      marginBottom: "20px",
      fontSize: "14px",
      lineHeight: "1.6",
      color: "#6b7280",
      overflow: "hidden",
    },
    eventMetaInfo: {
      marginBottom: "20px",
      padding: "12px",
      background: "#f9fafb",
      borderRadius: "8px",
      fontSize: "13px",
      color: "#6b7280",
    },
    eventMetaInfoDiv: {
      marginBottom: "5px",
    },
    eventMetaInfoStrong: {
      color: "#374151",
      fontWeight: "600",
    },
    eventMeta: {
      marginTop: "auto",
      textAlign: "right",
    },
    eventButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "12px 24px",
      background: "linear-gradient(135deg, #7c3aed, #a855f7)",
      color: "white",
      textDecoration: "none",
      borderRadius: "25px",
      fontSize: "14px",
      fontWeight: "500",
      textTransform: "capitalize",
      transition: "all 0.3s ease",
      boxShadow: "0 4px 12px rgba(124, 58, 237, 0.2)",
    },
    eventButtonHover: {
      background: "linear-gradient(135deg, #6d28d9, #9333ea)",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(124, 58, 237, 0.3)",
    },
    eventButtonIcon: {
      display: "flex",
      alignItems: "center",
    },
  };

  // State for hover effects
  const [isHovered, setIsHovered] = React.useState(false);
  const [isImageHovered, setIsImageHovered] = React.useState(false);
  const [isTitleHovered, setIsTitleHovered] = React.useState(false);
  const [isButtonHovered, setIsButtonHovered] = React.useState(false);

  const removeImagesFromHTML = (html) => {
    if (!html) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const images = doc.querySelectorAll("img");
    images.forEach((img) => img.remove());
    return doc.body.innerHTML;
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return "";
    const textContent = text.replace(/<[^>]*>/g, "");
    if (textContent.length <= maxLength) return text;
    return textContent.substring(0, maxLength) + "...";
  };

  const sanitizedDescription = removeImagesFromHTML(description || "");
  const truncatedDescription = truncateText(sanitizedDescription);

  return (
    <div
      style={{ ...styles.eventItemBox, ...(itemClass && {}) }}
      className={itemClass}
    >
      <div
        style={{
          ...styles.eventItem,
          ...(isHovered && styles.eventItemHover),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.eventThumb}>
          <Link
            to={`/event-details?eventId=${eventId}&&title=${encodeURIComponent(
              title || ""
            )}`}
          >
            <img
              src={eventImage || Image}
              alt={title || "Event image"}
              loading="lazy" // ADDED: Lazy loading for better performance
              style={{
                ...styles.eventImage,
                ...(isImageHovered && styles.eventImageHover),
              }}
              onMouseEnter={() => setIsImageHovered(true)}
              onMouseLeave={() => setIsImageHovered(false)}
              onError={(e) => {
                // ADDED: Fallback to default image on error
                e.target.src = Image;
              }}
            />
          </Link>
          <div style={styles.eventDate}>
            <span style={styles.eventDateSpan}>
              <i style={styles.eventDateNumber}>{eventDate}</i> <br />
              {eventMonth} <br />
              {eventYear}
            </span>
          </div>
        </div>

        <div style={styles.eventContent}>
          <h4 style={styles.eventTitle}>
            <Link
              to={`/event-details?eventId=${eventId}&&title=${encodeURIComponent(
                title || ""
              )}`}
              style={{
                ...styles.eventTitleLink,
                ...(isTitleHovered && styles.eventTitleLinkHover),
              }}
              onMouseEnter={() => setIsTitleHovered(true)}
              onMouseLeave={() => setIsTitleHovered(false)}
            >
              {title}
            </Link>
          </h4>

          <div style={styles.eventDescription}>
            {description ? (
              <div dangerouslySetInnerHTML={{ __html: truncatedDescription }} />
            ) : (
              <p>No description available.</p>
            )}
          </div>

          {/* Optional: Display event time and location if provided */}
          {(eventTime || eventLocation) && (
            <div style={styles.eventMetaInfo}>
              {eventTime && (
                <div style={styles.eventMetaInfoDiv}>
                  <span style={styles.eventMetaInfoStrong}>Time:</span>{" "}
                  {eventTime}
                </div>
              )}
              {eventLocation && (
                <div style={styles.eventMetaInfoDiv}>
                  <span style={styles.eventMetaInfoStrong}>Location:</span>{" "}
                  {eventLocation}
                </div>
              )}
            </div>
          )}

          <div style={styles.eventMeta}>
            <Link
              to={`/event-details?eventId=${eventId}&&title=${encodeURIComponent(
                title || ""
              )}`}
              style={{
                ...styles.eventButton,
                ...(isButtonHovered && styles.eventButtonHover),
              }}
              onMouseEnter={() => setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
            >
              Read More
              {hasArrow && (
                <i style={styles.eventButtonIcon}>
                  <RightArrow />
                </i>
              )}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleEvent;
